import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { type ButtonHTMLAttributes, forwardRef } from "react";

import { cn } from "@/lib/cn";

export const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-control font-semibold",
    "select-none transition-[background-color,border-color,color,box-shadow,transform] duration-(--duration-instant) ease-(--ease-standard) active:translate-y-px",
    "disabled:pointer-events-none disabled:opacity-50 aria-busy:pointer-events-none",
    "[&_svg]:size-5 [&_svg]:shrink-0 [&_svg]:stroke-[1.75]",
  ],
  {
    variants: {
      variant: {
        primary:
          "bg-accent text-on-accent hover:bg-accent-hover active:bg-accent-pressed aria-busy:bg-accent-hover",
        secondary:
          "border border-line bg-surface text-ink hover:bg-surface-2 active:bg-surface-3 aria-busy:bg-surface-2",
        ghost: "text-ink-2 hover:bg-surface-2 hover:text-ink active:bg-surface-3",
        danger: "bg-danger text-on-accent hover:opacity-90 active:opacity-80",
      },
      size: {
        // 44 px: the design's target floor (a11y.md §1); the inventory's 40 px was below it.
        md: "h-11 px-4 text-base",
        lg: "h-14 px-6 text-md",
        icon: "size-11 p-0",
      },
      block: { true: "flex w-full", false: "" },
    },
    defaultVariants: { variant: "primary", size: "md", block: false },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Render the child element (a Link) with the button's classes. */
  asChild?: boolean;
  /** Loading swaps the label for `loadingLabel` and sets aria-busy; never a spinner alone. */
  loading?: boolean;
  loadingLabel?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    block,
    asChild = false,
    loading = false,
    loadingLabel,
    children,
    ...props
  },
  ref,
) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : (props.type ?? "button")}
      className={cn(buttonVariants({ variant, size, block }), className)}
      aria-busy={loading || undefined}
      disabled={!asChild ? props.disabled || loading : undefined}
      {...props}
    >
      {loading && loadingLabel ? loadingLabel : children}
    </Comp>
  );
});
